import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { History, Loader2, Pencil, TrendingUp, CalendarClock, Gauge } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import api from '../../lib/api';
import { formatImporte } from '../../lib/utils';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const mesLabel = (p) => { const [y, m] = p.split('-'); return `${MESES[+m - 1]} ${y}`; };
const $ = (v) => (v ? `$ ${formatImporte(v, 2)}` : '—');

const TONOS = { slate: ['bg-slate-50 border-slate-200', 'text-slate-600'], emerald: ['bg-emerald-50 border-emerald-200', 'text-emerald-600'] };
const Chip = ({ icon: Icon, label, value, testid, tone = 'slate' }) => (
  <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${TONOS[tone][0]}`} data-testid={testid}>
    <Icon className={`w-4 h-4 ${TONOS[tone][1]}`} />
    <div className="leading-tight">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800">{value}</div>
    </div>
  </div>
);

const Semaforo = ({ m, p }) => (
  <span className="inline-flex items-center gap-1.5" data-testid={`historial-fc-semaforo-${p}`}
    title={m.cobrada ? 'Cobrada' : m.estado === 'cobro_parcial' ? 'Cobro parcial (no cobrada total)' : 'No cobrada'}>
    <span className={`w-3 h-3 rounded-full ${m.cobrada ? 'bg-emerald-500' : 'bg-red-500'} shadow-sm`} />
    <span className="text-xs text-slate-600">{m.cobrada ? 'Cobrada' : m.estado === 'cobro_parcial' ? 'Parcial' : m.estado === 'vencida' ? 'Vencida' : 'Pendiente'}</span>
  </span>
);

const Fila = ({ p, m, ultima }) => {
  const esUltima = p === ultima;
  return (
    <tr className={`border-t border-slate-100 ${esUltima ? 'bg-emerald-50' : ''}`} data-testid={`historial-fc-fila-${p}`}>
      <td className="px-3 py-2 font-medium whitespace-nowrap">
        {mesLabel(p)}
        {esUltima && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[10px] px-2 py-0.5" data-testid="historial-fc-ultima-badge"><TrendingUp className="w-3 h-3" />Actualización</span>}
      </td>
      {!m ? <td colSpan={4} className="px-3 py-2 text-center text-slate-300">Sin facturación</td> : (<>
        <td className="px-3 py-2 text-right font-mono whitespace-nowrap">
          <span className="inline-flex items-center gap-1">{m.manual && <Pencil className="w-3 h-3 text-amber-600" title="Monto modificado a mano" />}{$(m.neto)}</span>
        </td>
        <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{$(m.total)}</td>
        <td className="px-3 py-2 font-mono text-xs text-slate-700">{m.nro_factura || '—'}</td>
        <td className="px-3 py-2"><Semaforo m={m} p={p} /></td>
      </>)}
    </tr>
  );
};

export const HistorialFCButton = ({ edificioId, periodo, item }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const clave = item.suscriptor_id || item.unidad_id;
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get(`/facturacion/${edificioId}/historial-neto`, { params: { hasta: periodo, meses: 6, clave } })
      .then(r => setData(r.data)).catch(() => { toast.error('No se pudo cargar el historial'); setOpen(false); });
  }, [open, edificioId, periodo, clave]);
  const f = data?.filas?.[0];
  const ultima = f?.ultima_actualizacion;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title="Ver historial de facturas"
        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
        data-testid={`historial-fc-btn-${clave}`}>
        <History className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="historial-fc-dialog">
          <DialogHeader>
            <DialogTitle>Historial FC · {item.unidad_nombre}</DialogTitle>
            <DialogDescription>{item.nombre_comercial || item.inquilino} · últimos 6 meses</DialogDescription>
          </DialogHeader>
          {!data ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-10 text-slate-400" /> : !f ? (
            <p className="text-sm text-slate-500 py-6 text-center">Sin facturación en los últimos 6 meses.</p>
          ) : (<>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Chip icon={CalendarClock} label="Actualiza cada" value={f.es_cowork ? 'Cowork' : (f.frecuencia || 'Sin definir')} testid="historial-fc-frecuencia" />
              <Chip icon={Gauge} label="Índice" value={f.indice ? String(f.indice).toUpperCase() : 'Sin definir'} testid="historial-fc-indice" />
              <Chip icon={TrendingUp} tone="emerald" label="Última actualización" value={ultima ? mesLabel(ultima) : 'Sin aumentos desde el inicio'} testid="historial-fc-ultima" />
            </div>
            <div className="overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm" data-testid="historial-fc-tabla">
                <thead className="bg-slate-50">
                  <tr className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="text-left px-3 py-2">Mes</th>
                    <th className="text-right px-3 py-2">Neto</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-left px-3 py-2">N° FC</th>
                    <th className="text-left px-3 py-2">Cobro</th>
                  </tr>
                </thead>
                <tbody>{[...data.periodos].reverse().map(p => <Fila key={p} p={p} m={f.meses[p]} ultima={ultima} />)}</tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-1"><Pencil className="w-3 h-3 text-amber-600" /> monto modificado a mano · fila verde = mes de la última actualización</p>
          </>)}
        </DialogContent>
      </Dialog>
    </>
  );
};
